import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ChevronDown, Clock, MapPin } from "lucide-react";
import { MaskedLine } from "./Reveal";
import { waLink, DEFAULT_WA_MESSAGE } from "../../lib/site";
import { WhatsAppIcon } from "./Header";

const HERO_IMG =
  "https://images.unsplash.com/photo-1503376780353-7e6692767b70?auto=format&fit=crop&w=2000&q=80";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative min-h-screen flex flex-col justify-center overflow-hidden">
      <motion.div style={{ y: bgY }} className="absolute inset-0 -z-10">
        <img src={HERO_IMG} alt="Carro esportivo na penumbra" className="w-full h-[120%] object-cover object-center opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#090a0c]/60 via-[#090a0c]/45 to-[#090a0c]" />
        <div className="absolute inset-0 glow-amber" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="max-w-7xl mx-auto px-5 sm:px-8 w-full pt-32 pb-20">
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="inline-flex items-center gap-2.5 border border-[#e8112d]/30 bg-[#e8112d]/10 rounded-full px-4 py-1.5 mb-8"
          data-testid="hero-live-badge"
        >
          <span className="live-dot w-2 h-2 rounded-full bg-[#25D366]" />
          <span className="font-tech text-[11px] sm:text-xs uppercase tracking-[0.22em] text-[#ff9aa6]">
            Socorro elétrico · 24h
          </span>
        </motion.div>

        <h1 className="font-bold uppercase tracking-tight leading-[1.12] text-3xl sm:text-4xl lg:text-5xl" data-testid="hero-headline">
          <MaskedLine delay={0.25}>Está com problema</MaskedLine>
          <MaskedLine delay={0.4}>elétrico no seu carro?</MaskedLine>
          <MaskedLine delay={0.55}>
            <span className="text-[#e8112d] text-glow">A NVS resolve</span>
          </MaskedLine>
          <MaskedLine delay={0.7}>no seu local.</MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.85 }}
          className="mt-7 max-w-xl text-base sm:text-lg text-slate-400 leading-relaxed"
          data-testid="hero-subheadline"
        >
          Auto elétrica móvel 24 HRS com atendimento no seu local — fazemos o reparo na hora.{" "}
          <span className="text-white font-semibold">
            Alternador, motor de arranque, bomba de combustível e elétrica em geral.
          </span>
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 1.0 }}
          className="mt-10 flex flex-col sm:flex-row gap-4"
        >
          <a
            href={waLink(DEFAULT_WA_MESSAGE)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-urgent-whatsapp-btn"
            className="live-dot inline-flex items-center justify-center gap-3 bg-[#25D366] hover:bg-[#20BD5A] text-black font-bold text-base px-8 py-4 rounded-full transition-all duration-300 hover:scale-[1.03] hover:shadow-2xl hover:shadow-[#25D366]/30"
          >
            <WhatsAppIcon className="w-5 h-5" />
            Solicitar técnico agora
          </a>
          <a
            href="#servicos"
            data-testid="hero-services-anchor-btn"
            className="inline-flex items-center justify-center gap-2 border border-white/15 hover:border-[#e8112d]/60 text-slate-200 hover:text-white font-semibold text-base px-8 py-4 rounded-full transition-all duration-300 hover:bg-white/5"
          >
            Ver serviços técnicos
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 1.25 }}
          className="mt-14 grid grid-cols-1 sm:grid-cols-2 gap-px bg-white/10 border border-white/10 rounded-2xl overflow-hidden max-w-xl"
          data-testid="hero-stats"
        >
          {[
            { icon: Clock, value: "20 min", label: "chegada média" },
            { icon: MapPin, value: "BC e região", label: "cobertura total" },
          ].map((s, i) => (
            <div key={i} className="bg-[#211016]/95 px-6 py-5 flex items-center gap-4">
              <s.icon className="w-5 h-5 text-[#e8112d] shrink-0" />
              <div>
                <p className="font-display font-bold text-lg leading-none">{s.value}</p>
                <p className="text-xs text-slate-500 mt-1.5 uppercase tracking-wider font-tech">{s.label}</p>
              </div>
            </div>
          ))}
        </motion.div>
      </motion.div>

      <motion.div
        animate={{ y: [0, 8, 0] }}
        transition={{ repeat: Infinity, duration: 2.2, ease: "easeInOut" }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-slate-500"
      >
        <ChevronDown className="w-6 h-6" />
      </motion.div>
    </section>
  );
}
